import { writable } from 'svelte/store';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  dismissible: boolean;
}

export interface ToastOptions {
  type?: ToastType;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

function createToastStore() {
  const { subscribe, update } = writable<Toast[]>([]);

  const generateId = (): string => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const remove = (id: string): void => {
    update(toasts => toasts.filter(toast => toast.id !== id));
  };

  const add = ({
    type = 'info',
    message,
    duration = 3500,
    dismissible = false
  }: ToastOptions): string => {
    const id = generateId();
    const toast: Toast = {
      id,
      type,
      message,
      dismissible
    };

    update(toasts => [...toasts, toast]);

    if (duration) {
      setTimeout(() => {
        remove(id);
      }, duration);
    }

    return id;
  };

  const success = (message: string, options: Omit<ToastOptions, 'type' | 'message'> = {}) =>
    add({ type: 'success', message, ...options });

  const error = (message: string, options: Omit<ToastOptions, 'type' | 'message'> = {}) =>
    add({ type: 'error', message, ...options });

  const warning = (message: string, options: Omit<ToastOptions, 'type' | 'message'> = {}) =>
    add({ type: 'warning', message, ...options });

  const info = (message: string, options: Omit<ToastOptions, 'type' | 'message'> = {}) =>
    add({ type: 'info', message, ...options });

  const clear = (): void => {
    update(() => []);
  };

  return {
    subscribe,
    add,
    remove,
    clear,
    success,
    error,
    warning,
    info
  };
}

export const toasts = createToastStore();